
import { Button } from "@/components/ui/button";
import { CheckCircle2, ShoppingCart, Sparkles, Wand2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import React from "react";
import { useTranslation } from 'react-i18next';

const STYLE_OPTIONS = [
  { id: "cartoon", label: "Cartoon" },
  { id: "Realistic", label: "Realistic" },
  { id: "2d", label: "2D" },
  { id: "animation", label: "Animation" },
] as const;

type StyleId = typeof STYLE_OPTIONS[number]["id"];

const ITEM_PREVIEWS: Array<{
  id: string;
  name: string;
  images: Record<StyleId, string>;
}> = [
    {
      id: "apple",
      name: "Apple",
      images: {
        cartoon: "/styles/cartoon-apple.jpg",
        Realistic: "/styles/photo-apple.jpg",
        "2d": "/styles/2d-apple.jpg",
        "animation": "/styles/3d-apple.jpg",
      },
    },
    {
      id: "broccoli",
      name: "Broccoli",
      // Using available demo assets; replace with eggs variants when ready
      images: {
        cartoon: "/styles/cartoon-broccoli.jpg",
        Realistic: "/styles/photo-broccoli.jpg",
        "2d": "/styles/2d-broccoli.jpg",
        "animation": "/styles/3d-broccoli.jpg",
      },
    },
  ];

const Hero: React.FC = () => {
  const navigate = useNavigate();
  const [activeItemId, setActiveItemId] = React.useState<string>(ITEM_PREVIEWS[0].id);
  const [activeStyle, setActiveStyle] = React.useState<StyleId>(STYLE_OPTIONS[0].id);
  const [imageLoaded, setImageLoaded] = React.useState<boolean>(false);
  const { t } = useTranslation();

  const scrollToDemo = () => {
    const demoSection = document.getElementById("demo");
    if (demoSection) {
      demoSection.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const activeItem = ITEM_PREVIEWS.find((i) => i.id === activeItemId) ?? ITEM_PREVIEWS[0];
  const currentImage = activeItem.images[activeStyle] ?? activeItem.images[STYLE_OPTIONS[0].id];

  React.useEffect(() => {
    // Reset load state to trigger fade-in on item/style change
    setImageLoaded(false);
  }, [activeItemId, activeStyle]);

  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-sky-50 via-emerald-50 to-yellow-50">
      <div className="absolute inset-0 pointer-events-none opacity-40" aria-hidden="true" />
      <div className="container mx-auto px-4 py-12 md:py-16 lg:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Copy */}
          <div>
            <div className="hidden sm:inline-flex items-center gap-2 rounded-full border bg-white/60 px-3 py-1 text-sm text-gray-700 shadow-sm backdrop-blur">
              <Sparkles className="h-4 w-4 text-emerald-600" />
              {t('hero.badge')}
            </div>

            <h1 className="mt-3 sm:mt-4 text-3xl md:text-6xl font-extrabold tracking-tight text-gray-900">{t('hero.title')}</h1>
            <p className="mt-3 sm:mt-4 text-base md:text-xl text-gray-700 leading-relaxed sm:block">{t('hero.subtitle')}</p>

            <ul className="mt-6 space-y-3 text-gray-800">
              <li className="hidden sm:flex items-center gap-3"><CheckCircle2 className="h-5 w-5 text-emerald-600" />{t('hero.benefit1')}</li>
              <li className="hidden sm:flex items-center gap-3"><CheckCircle2 className="h-5 w-5 text-emerald-600" />{t('hero.benefit2')}</li>
              <li className="hidden sm:flex items-center gap-3"><CheckCircle2 className="h-5 w-5 text-emerald-600" />{t('hero.benefit3')}</li>
            </ul>

            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <Button onClick={scrollToDemo} className="bg-gradient-to-r from-emerald-500 to-sky-500 hover:from-emerald-600 hover:to-sky-600 text-white px-8 py-6 text-lg rounded-full shadow-lg transition-all duration-300">
                <Wand2 className="mr-2 h-5 w-5" /> {t('hero.ctaDemo')}
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate("/login")}
                className="border-2 border-emerald-500 text-emerald-600 hover:bg-emerald-50 px-8 py-6 text-lg rounded-full"
              >
                <ShoppingCart className="mr-2 h-5 w-5" /> {t('hero.ctaStart')}
              </Button>
            </div>

            <div className="mt-4 sm:mt-6 text-sm text-gray-600 hidden sm:block">{t('hero.lovedBy')}</div>
          </div>

          {/* Interactive preview */}
          <div className="relative">
            <div className="mx-auto w-full max-w-md rounded-3xl bg-white/70 p-4 shadow-xl backdrop-blur">
              {/* Item tabs */}
              <div className="mt-4 text-center text-sm text-gray-600 sm:block">{t('hero.styleNote')}</div>
              <div className="flex items-center justify-center gap-2 pb-3">
                {ITEM_PREVIEWS.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => setActiveItemId(item.id)}
                    className={`rounded-full px-3 py-1.5 text-sm transition ${activeItemId === item.id
                      ? "bg-sky-600 text-white shadow"
                      : "bg-white text-gray-700 border hover:bg-gray-50"
                      }`}
                    aria-pressed={activeItemId === item.id}
                    aria-label={t('hero.aria.previewItem', { itemName: t(`hero.item.${item.id}`) })}
                  >
                    {t(`hero.item.${item.id}`)}
                  </button>
                ))}
              </div>

              <div className="mt-3 aspect-video w-full overflow-hidden rounded-2xl border bg-white relative">
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  key={`${activeItemId}-${activeStyle}`}
                  src={currentImage}
                  alt={t('hero.alt.preview', { styleLabel: t(`hero.style.${(String(activeStyle).toLowerCase())}`), itemName: t(`hero.item.${activeItem.id}`) })}
                  className={`h-full w-full object-cover transition-opacity duration-300 ease-in-out ${imageLoaded ? "opacity-100" : "opacity-0"}`}
                  loading="eager"
                  onLoad={() => setImageLoaded(true)}
                />
                {!imageLoaded && (
                  <div className="absolute inset-0 animate-pulse bg-gradient-to-br from-gray-100 to-gray-200" aria-hidden="true" />
                )}
              </div>

              <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
                {STYLE_OPTIONS.map((s, idx) => (
                  <button
                    key={s.id}
                    onClick={() => setActiveStyle(s.id)}
                    className={`rounded-full px-4 py-2 text-sm transition ${activeStyle === s.id
                      ? "bg-emerald-600 text-white shadow"
                      : "bg-white text-gray-700 border hover:bg-gray-50"
                      } ${idx >= 2 ? "hidden sm:inline-flex" : "inline-flex"}`}
                    aria-pressed={activeStyle === s.id}
                    aria-label={t('hero.aria.switchStyle', { styleLabel: t(`hero.style.${(String(s.id).toLowerCase())}`) })}
                  >
                    {t(`hero.style.${(String(s.id).toLowerCase())}`)}
                  </button>
                ))}
              </div>


            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
